import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import styles from './Navigation.module.css'

const navItems = [
  { path: '/', label: 'Dashboard', description: 'Capture links and track summaries' },
  { path: '/share-complete', label: 'Share inbox', description: 'Review incoming share targets' },
]

function Navigation() {
  const location = useLocation()
  const { user, isAuthenticated, isAuthenticating, isReady, requiresConfiguration, signInWithGoogle, signOut } = useAuth()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/share')
    }
    return location.pathname === path
  }

  const renderAuthSection = () => {
    if (!isReady) {
      return <p className={styles.authPreparing}>Preparing sign-in…</p>
    }

    if (requiresConfiguration) {
      return (
        <div className={styles.authButtons}>
          <div className={styles.authNotice}>
            <p className={styles.authHeading}>Authentication setup needed</p>
            <p>
              Add <code className={styles.code}>VITE_GOOGLE_CLIENT_ID</code> to enable Google sign-in. Using demo mode for now.
            </p>
          </div>
          <button
            type='button'
            onClick={signInWithGoogle}
            className={`${styles.authButton} ${styles.demoAuthButton}`}
          >
            Use demo account
          </button>
        </div>
      )
    }

    if (!isAuthenticated) {
      return (
        <div className={styles.authButtons}>
          <p className={styles.authNotice}>Connect your Google account to sync saves and summaries.</p>
          <button
            type='button'
            onClick={signInWithGoogle}
            disabled={isAuthenticating}
            className={styles.authButton}
          >
            {isAuthenticating ? 'Signing in…' : 'Sign in with Google'}
          </button>
        </div>
      )
    }

    return (
      <div className={styles.userSection}>
        <div className={styles.userRow}>
          <div className={styles.avatar}>{user?.name?.charAt(0)?.toUpperCase() ?? 'U'}</div>
          <div className={styles.userDetails}>
            <p className={styles.userName}>{user?.name ?? 'Signed in'}</p>
            <p className={styles.userEmail}>{user?.email}</p>
          </div>
        </div>
        <button type='button' onClick={signOut} className={styles.secondaryButton}>
          Sign out
        </button>
      </div>
    )
  }

  return (
    <nav className={styles.nav}>
      <div className={styles.topBar}>
        <div>
          <h1 className={styles.brandTitle}>FTS Library</h1>
          <p className={styles.brandDescription}>Quick capture for your Chrome extension, PWA, and web dashboard.</p>
        </div>
        <button
          type='button'
          className={styles.mobileMenuButton}
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-expanded={isMenuOpen}
          aria-controls='nav-content'
        >
          {isMenuOpen ? 'Close' : 'Menu'}
        </button>
      </div>

      <div id='nav-content' className={`${styles.content} ${isMenuOpen ? styles.contentOpen : ''}`}>
        <ul className={styles.navList}>
          {navItems.map((item) => {
            const active = isActive(item.path)
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`${styles.navLink} ${active ? styles.navLinkActive : ''}`}
                >
                  <span className={styles.navLinkLabel}>{item.label}</span>
                  <span className={styles.navLinkDescription}>{item.description}</span>
                </Link>
              </li>
            )
          })}
        </ul>

        <div className={styles.footer}>{renderAuthSection()}</div>
      </div>
    </nav>
  )
}

export default Navigation
